import { AlertTriangle, CheckCircle, ChevronDown, Clock, Layers, XCircle } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { nativeBatch } from 'sprites-types/workflow/tools/nativeBatch'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import { SmartValueDisplay } from '../shared/SmartValueDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function BatchResultItem({
  item,
  index,
  className,
}: {
  item: any
  index: number
  className?: string
}) {
  const status = item.status || item.success ? 'success' : item.error ? 'error' : 'unknown'
  const result = item.result || item.data || item.output
  const error = item.error || item.errorMessage
  const duration = item.duration || item.executionTime

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />
      default:
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />
    }
  }

  const getStatusBg = () => {
    switch (status) {
      case 'success':
        return 'bg-green-50 border-green-200'
      case 'error':
        return 'bg-red-50 border-red-200'
      default:
        return 'bg-yellow-50 border-yellow-200'
    }
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-2 flex items-center gap-3">
          <div className={`rounded-full p-1.5 ${getStatusBg()}`}>{getStatusIcon()}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">Item {index + 1}</div>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="capitalize">{status}</span>
              {duration && (
                <>
                  <span>•</span>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{duration}ms</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-2 rounded-lg border border-red-200 bg-red-50 p-2">
            <div className="text-xs text-red-800">{error}</div>
          </div>
        )}

        {result && <SmartValueDisplay value={result} maxLength={200} />}
      </div>
    </div>
  )
}

function NativeBatchDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(nativeBatch, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const results = Array.isArray(result)
    ? result
    : (result as any)?.results || (result as any)?.items || (result ? [result] : [])
  const successCount = results.filter(
    (item: any) => item.status === 'success' || (item.success && !item.error)
  ).length
  const errorCount = results.filter((item: any) => item.status === 'error' || item.error).length

  if (results.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Layers className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No batch results found</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Layers className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                Batch Processing Results
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {results.length} items processed • {successCount} success • {errorCount} errors
              </div>
            </div>
            <div className="flex items-center gap-2">
              {successCount > 0 && (
                <div className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                  {successCount}
                </div>
              )}
              {errorCount > 0 && (
                <div className="rounded-full bg-red-100 px-2 py-1 text-xs text-red-800">
                  {errorCount}
                </div>
              )}
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {results.map((item: any, index: number) => (
            <BatchResultItem key={index} item={item} index={index} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadNativeBatch(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function nativeBatchToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(nativeBatch, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const results = Array.isArray(result)
    ? result
    : (result as any)?.results || (result as any)?.items || (result ? [result] : [])

  if (results.length === 0) {
    return `## Batch Processing Results

No batch results found.

---
`
  }

  const successCount = results.filter(
    (item: any) => item.status === 'success' || (item.success && !item.error)
  ).length
  const errorCount = results.filter((item: any) => item.status === 'error' || item.error).length

  let markdown = `## Batch Processing Results (${results.length} items)

**Summary:**
- ✅ ${successCount} successful
- ❌ ${errorCount} failed

### Results:

`

  results.forEach((item: any, index: number) => {
    const status = item.status || (item.success ? 'success' : item.error ? 'error' : 'unknown')
    const result = item.result || item.data || item.output
    const error = item.error || item.errorMessage

    markdown += `#### Item ${index + 1} (${status})

`

    if (error) {
      markdown += `**Error:** ${error}\n\n`
    }

    if (result) {
      markdown += `**Result:**\n\`\`\`\n${typeof result === 'string' ? result : JSON.stringify(result, null, 2)}\n\`\`\`\n\n`
    }

    markdown += '---\n\n'
  })

  return markdown
}

export const NativeBatchDisplay = createWorkflowToolComponent(
  NativeBatchDisplayComponent,
  downloadNativeBatch,
  nativeBatchToMarkdown
)
